import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Divider, IconButton, Grid, Typography, Box, Button } from '@mui/material';
import { colors, PageLoader, CustomPagination } from 'remote/global';
import { MoreVert } from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { DataGridOverlay } from 'utils/component-utils';
import { ROUTES } from 'utils/data/routes';
import { useOperatorsUserMessagingGroupsQuery } from 'api/queries/operators';
import CreateNewGroupModal from 'components/modals/CreateNewGroupModal';

function MessagesGroupsView() {
  const { data: messageGroups, isSuccess } = useOperatorsUserMessagingGroupsQuery();
  const [isCreateNewGroupModalOpen, setIsCreateNewGroupModalOpen] = useState(false);
  const [page, setPage] = useState(0);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('MessagesView.group.current.table.header.name') as string,
      flex: 2,
      renderCell: ({ row }) => {
        return (
          <Box>
            <Typography
              sx={{
                color: colors.gray2,
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '20px',
              }}
            >
              {row.name}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: ' ',
      align: 'right',
      headerName: '',
      flex: 1,
      minWidth: 200,
      renderCell: ({ row }) => (
        <Box>
          <IconButton
            size='large'
            aria-label='edit member'
            aria-controls='edit-member'
            sx={{
              padding: 0,
              color: colors.uiBlue600,
              marginRight: '12px',
            }}
            onClick={() => {
              navigate(
                [
                  ROUTES.BaseRoutes.MAIN,
                  ROUTES.MainRoutes.MESSAGES,
                  ROUTES.MessagesRoutes.GROUP,
                  row.id,
                ].join('/')
              );
            }}
          >
            <MoreVert />
          </IconButton>
        </Box>
      ),
    },
  ];

  const handlePageChange = (pageNo: number) => {
    setPage(pageNo);
  };

  const handleClearCreateNewGroupModalState = () => {
    setIsCreateNewGroupModalOpen(false);
  };

  if (!isSuccess) return null;

  return (
    <>
      <CreateNewGroupModal
        open={isCreateNewGroupModalOpen}
        setOpen={setIsCreateNewGroupModalOpen}
        onClose={handleClearCreateNewGroupModalState}
      />
      <Grid container spacing={4}>
        <Grid item xs={12} lg={6}>
          <Typography
            color={colors.uiBlue600}
            mb='12px'
            fontWeight='600'
            fontSize='18px'
            style={{ textAlign: 'left' }}
          >
            {t('MessagesView.group.create.title')}
          </Typography>
          <Typography
            fontSize='16px'
            variant='body2'
            mb='29px'
            color={colors.gray1}
            style={{ textAlign: 'left' }}
          >
            {t('MessagesView.group.create.subtitle')}
          </Typography>
        </Grid>
        <Grid item xs={12} lg={6} display='flex' alignItems='center' justifyContent='flex-end'>
          <Button onClick={() => setIsCreateNewGroupModalOpen(true)}>
            {t('MessagesView.group.actions.createGroup')}
          </Button>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Divider style={{ width: '100%', marginTop: '24px', marginBottom: '24px' }} />
      </Grid>
      <Grid item container xs={12}>
        <Grid item xs={12}>
          <Typography
            color={colors.uiBlue600}
            mb='12px'
            fontWeight='600'
            fontSize='18px'
            style={{ textAlign: 'left' }}
          >
            {t('MessagesView.group.current.title')}
          </Typography>
          <Typography
            fontSize='16px'
            variant='body2'
            mb='29px'
            color={colors.gray1}
            style={{ textAlign: 'left' }}
          >
            {t('MessagesView.group.current.subtitle')}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <DataGrid
            rows={messageGroups || []}
            columns={columns}
            paginationModel={{
              page,
              pageSize: 5,
            }}
            autoHeight
            columnHeaderHeight={52}
            disableColumnSelector
            disableColumnMenu
            disableRowSelectionOnClick
            slots={{
              noRowsOverlay: () => DataGridOverlay(t('Placeholder.noRows')),
              noResultsOverlay: () => DataGridOverlay(t('Placeholder.noResults')),
              loadingOverlay: PageLoader,
              pagination: (props) =>
                CustomPagination({
                  ...props,
                  t,
                  pageCount: Math.ceil(messageGroups.length / 5),
                  pageNo: page,
                  onChange: (_, pageNo) => {
                    handlePageChange(pageNo - 1);
                  },
                }),
            }}
          />
        </Grid>
      </Grid>
    </>
  );
}

export default MessagesGroupsView;
